import React, { useState, useEffect, useRef, useCallback } from 'react';
import { authFetch, API } from './api';
import PackingWindow, { PackedLine } from './PackingWindow';
import { makeScanGuard } from './scanGuard';
import { useScanWedge } from './scanWedge';
import { OrderNo } from './OrderNo';
import { detectZebraIfEnabled, deliverLabel } from './labelPrint';
import { fmtWh } from './time';
import { track, trackLang, trackActor } from './pp/track';
import SpeedChipLive from './pp/SpeedChip';
import { packingText, cartFor, CartRule } from './packingText';
import { useReprintApproval, useManualPackApproval } from './pp2/ReprintApproval';

interface OrderInfo {
  id: string;
  /** Country order number (SE61709) — what the delivery note, the label and the
   *  shop all quote. `id` stays the key every API call uses. */
  order_number?: string;
  customer: string;
  carrier: string;
  /** destination country — decides the dispatch cart together with the label's carrier */
  country?: string;
}

// 'instruction' (Fernando, 2026-09-28): the flow routes the order itself and tells the packer
// what to do with the bag — an item is missing (open bag, grey box) or there is no label yet
// (seal it, the order went to Label issue). In the packer's own language.
type Phase = 'scan_order' | 'packing' | 'label' | 'instruction';
type Instruction =
  | { kind: 'missing'; tab: string }
  | { kind: 'nolabel'; moved: boolean; error?: string }
  // too big for a bag (Fernando 1.11): pressed at the label gate; the packer picks grey box 1/2/3, the order moves
  // to Too Big as a diverted order of its round, a manager splits it at a station
  | { kind: 'too_big'; box: number | null; moved: boolean; error?: string; busy?: boolean };

// Pack-time label print (Fernando, 2026-09-06): when the delivery note is
// scanned and the order is being packed for the FIRST time (tab Printed or
// Picker 2), the server hands over the stored, never-output label and it goes
// to the printer right then — no more printing the whole stack in the morning.
// The server decides (POST /orders/{id}/pack-label); this is only what the
// packer is told. A Zebra Browser Print device prints silently; without one a
// click is needed, because a popup can only be opened from a click, not a scan.
// Archived order (Fernando, 2026-09-21): its labels left with the parcel, so
// the scan means a re-ship — the server answers `dispatched`, the banner says
// "buying a new label", and a second request with buy_new asks the carrier.
type PackLabel =
  | { status: 'checking' }
  | { status: 'buying' }            // nothing stored — the server is buying one now (2026-09-29)
  | { status: 'sent'; label: any; bought?: boolean }
  | { status: 'ready'; label: any; data: any; note?: string; bought?: boolean }
  | { status: 'already_printed'; label: any; at: string | null; by: string | null }
  | { status: 'no_label' }
  | { status: 'stale'; detail: string }
  | { status: 'not_first_packing'; tabLabel: string }
  | { status: 'dispatched'; at: string | null; previous: { tracking: string; carrier: string } | null }
  | { status: 'buy_failed'; at: string | null; detail: string }
  | { status: 'error'; detail: string };

const fmtTime = (s: string | null | undefined) =>
  s ? fmtWh(s, 'time') : '';

/** Guided P&P (pp2/StationApp, 2026-10-17): the server clears every delivery-note scan first (the order must be in
 *  a picked guided round, packed at this station), the label goes to this station's Zebra through the print agent,
 *  a badge scanned here is the station's (intercept), and the round's progress + Finish sit above the scan box. */
export interface GuidedPacking {
  roundId: number;
  manualGate?: (line: { line_id?: string; product_code?: string; name?: string; order_id?: string }) => Promise<boolean>;
  intercept?: (code: string) => boolean;
  onProgress?: () => void;
  header?: React.ReactNode;
  /** a manager's fix at a station (2.11): the order is processed as soon as its label is scanned; the answer is
   *  shown with the dispatch cart */
  onLabelScanned?: (orderId: string, code: string) => Promise<{ ok: boolean; text: string } | null>;
  /** a fix: the order cannot go out now (a piece still missing, no label) — it leaves the fix, in its tab */
  onReleased?: (orderId: string, reason: string) => void;
  /** the fix orders whose delivery note was printed new — "put the NEW note in the bag" */
  newNotes?: string[];
  /** every phase change: 'scan_order' = between two orders (the last label scanned) — the station calls the Packeta
   *  handover only then (Fernando 3.11: "allow the PP-er to finish the order they are packing") */
  onPhase?: (phase: string) => void;
}

interface Props {
  onClose: () => void;
  guided?: GuidedPacking;
}

// Normalize a scanned/stored tracking code for comparison: strip spaces and
// punctuation, uppercase. Carrier label barcodes sometimes carry minor prefix
// padding vs. the stored tracking number, so we compare on the bare value.
const normLabel = (s: string) => (s || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();

// THE label-matching rule — used by the label gate and by the note step's
// "that is the label you just scanned" check, so the two can never disagree.
// Exact, or (both ≥ 8 chars) either contains the other: carrier barcodes pad
// the tracking number with routing prefixes/suffixes.
const labelMatches = (codes: string[], scannedRaw: string) => {
  const scanned = normLabel(scannedRaw);
  if (!scanned) return false;
  return codes.map(normLabel).filter(Boolean).some(t =>
    t === scanned ||
    (t.length >= 8 && scanned.length >= 8 && (t.includes(scanned) || scanned.includes(t))));
};

export default function PackingSession({ onClose, guided }: Props) {
  const guidedRef = useRef(guided);
  guidedRef.current = guided;
  // Print again is a reprint: Tier 1 needs a manager's PIN or badge (Fernando 19.10)
  const reprintApproval = useReprintApproval();
  // Pack without scanning on the old packing screen: without the right, a manager's PIN or badge (Fernando 20.10 —
  // the station asks the same through guided.manualGate)
  const manualApproval = useManualPackApproval();
  const [phase, setPhase]               = useState<Phase>('scan_order');
  useEffect(() => { guidedRef.current?.onPhase?.(phase); }, [phase]);
  const [currentOrder, setCurrentOrder] = useState<OrderInfo | null>(null);
  // the last order loaded in this session — the speed chip follows its round (2026-09-28)
  const [lastOrderId, setLastOrderId]   = useState<string | null>(null);
  const [trackingNumbers, setTrackingNumbers] = useState<string[]>([]);
  // The label codes of the order that was JUST finished. The step after the
  // label gate is "scan the next delivery note", and a scanner double-fire (or
  // a packer re-scanning the parcel) sends the label there; it must never be
  // looked up as an order number. Cleared once the next order loads.
  const [lastLabelCodes, setLastLabelCodes] = useState<string[]>([]);
  const [scanInput, setScanInput]       = useState('');
  const [error, setError]               = useState('');
  const [hint, setHint]                 = useState('');   // grey, not an error
  const [loading, setLoading]           = useState(false);
  const [beginnerMultiQty, setBeginnerMultiQty] = useState(false);   // pp_guided.scan_note (Fernando 21.10)
  const [tips, setTips] = useState<any>(null);                         // pp_tips state for this packer (21.10)
  const [specialPlaces, setSpecialPlaces] = useState<{ code: string; note?: string | null }[]>([]);
  // a new packer ticks "the delivery note is in the parcel" before the label scan counts (pp_tips note_in_parcel, 21.10)
  const [noteTick, setNoteTick] = useState(false);
  const noteTickRef = useRef(false);
  const tipsRef = useRef<any>(null);
  tipsRef.current = tips;
  // the tablet's Can't find for this order (21.10): shown at packing, never an automatic move
  const [shortLines, setShortLines] = useState<{ line_id: string; product_code: string; location: string | null; qty: number }[]>([]);
  const [instruction, setInstruction]   = useState<Instruction | null>(null);
  // the dispatch cart of the order just labelled, shown until the next note is scanned
  // fix (2.11): the order's Process at its label scan — null while it runs, then the answer
  const [cartCard, setCartCard]         = useState<{ order: string; cart: string; fix?: { ok: boolean | null; text: string } } | null>(null);
  // 1.11: the gate reached by a LABEL scan at "Scan order barcode" (the order was packed, its gate lost) — what to do
  const [gateNote, setGateNote]         = useState('');
  const [carts, setCarts]               = useState<CartRule[]>([]);
  // which label (carrier) each scannable code belongs to — the cart follows the label scanned
  const [labelCarriers, setLabelCarriers] = useState<Record<string, string>>({});
  const tx = packingText(trackLang());
  useEffect(() => {
    authFetch(`${API}/settings/dispatch-carts`).then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.rules) setCarts(d.rules); }).catch(() => {});
  }, []);
  const inputRef    = useRef<HTMLInputElement>(null);
  const timerRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dupRef      = useRef(makeScanGuard());

  // Printer detection once per session (a 2 s probe), not once per order.
  const zebraRef    = useRef<any | null>(null);
  const zebraReady  = useRef<Promise<void> | null>(null);
  useEffect(() => {
    zebraReady.current = detectZebraIfEnabled()
      .then(z => { zebraRef.current = z; })
      .catch(() => { zebraRef.current = null; });
  }, []);
  const [packLabel, setPackLabel] = useState<PackLabel | null>(null);
  // The label request in flight for the current order, and the codes as they are NOW:
  // when nothing was stored the server buys during the request, and a one-item order is
  // packed faster than a carrier answers — the pack-complete step waits for it (2026-09-29).
  const labelRequest = useRef<Promise<void> | null>(null);
  const trackingRef  = useRef<string[]>([]);
  useEffect(() => { trackingRef.current = trackingNumbers; }, [trackingNumbers]);

  const requestPackLabel = useCallback((orderId: string, hasStoredLabel: boolean) => {
    const p = requestPackLabelInner(orderId, hasStoredLabel);
    labelRequest.current = p;
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const requestPackLabelInner = async (orderId: string, hasStoredLabel: boolean) => {
    // no stored label → the server buys one inside this request; say so at once
    setPackLabel(hasStoredLabel ? { status: 'checking' } : { status: 'buying' });
    try {
      if (!guided && zebraReady.current) await zebraReady.current;
      const zebra = guided ? null : zebraRef.current;
      const ask = async (buyNew: boolean) => {
        const res = await authFetch(`${API}/orders/${encodeURIComponent(orderId)}/pack-label`, {
          method: 'POST',
          // guided: the label goes to this station's Zebra through the print agent (print_queue)
          body: JSON.stringify(guided ? { format: 'zpl', deliver: 'agent', ...(buyNew ? { buy_new: true } : {}) }
                                      : { format: zebra ? 'zpl' : 'pdf', ...(buyNew ? { buy_new: true } : {}) }),
        });
        const d = await res.json().catch(() => ({} as any));
        return { res, d };
      };
      let { res, d } = await ask(false);
      if (res.ok && d.status === 'dispatched') {
        // The banner is up while the carrier is asked; the second request buys.
        setPackLabel({ status: 'dispatched', at: d.dispatched_at || null, previous: d.previous_label || null });
        ({ res, d } = await ask(true));
      }
      if (!res.ok) { setPackLabel({ status: 'error', detail: d.detail || `HTTP ${res.status}` }); return; }
      switch (d.status) {
        case 'printed':
          if (d.bought) {
            // The label gate compares against the codes loaded WITH the order — a
            // label bought just now is not among them. Reload them (get_order also
            // decodes the barcodes); fall back to the tracking number.
            try {
              const r2 = await authFetch(`${API}/orders/${encodeURIComponent(orderId)}`);
              const o2 = r2.ok ? await r2.json() : null;
              const codes = (o2?.labels || []).filter((l: any) => !l.cancelled_at).flatMap((l: any) =>
                [l.tracking_number, l.tracking_alt, ...(l.scan_codes || [])]).filter(Boolean);
              setTrackingNumbers(prev => Array.from(new Set([...prev, ...codes, d.label?.tracking].filter(Boolean))));
            } catch {
              setTrackingNumbers(prev => d.label?.tracking ? [...prev, d.label.tracking] : prev);
            }
          }
          if (d.print_job) {
            // queued on this station's Zebra; the agent prints it within seconds (Settings → Printing shows a failure)
            setPackLabel({ status: 'sent', label: d.label, bought: !!d.bought });
            return;
          }
          if (guided && d.print_job_error) {
            setPackLabel({ status: 'ready', label: d.label, data: d, bought: !!d.bought, note: d.print_job_error });
            return;
          }
          if (zebra && d.zpl) {
            const ok = await deliverLabel(null, d, zebra);
            if (ok) { setPackLabel({ status: 'sent', label: d.label, bought: !!d.bought }); return; }
            setPackLabel({ status: 'ready', label: d.label, data: d, bought: !!d.bought,
                           note: 'The printer did not answer — print the label from here' });
            return;
          }
          setPackLabel({ status: 'ready', label: d.label, data: d, bought: !!d.bought });
          return;
        case 'already_printed':
          setPackLabel({ status: 'already_printed', label: d.label,
                         at: d.sent_to_printer_at || null, by: d.sent_to_printer_by || null });
          return;
        case 'buy_failed':
          setPackLabel({ status: 'buy_failed', at: d.dispatched_at || null, detail: d.detail || 'the carrier refused' });
          return;
        case 'no_label':          setPackLabel({ status: 'no_label' }); return;
        case 'stale':             setPackLabel({ status: 'stale', detail: d.detail || '' }); return;
        case 'not_first_packing': setPackLabel({ status: 'not_first_packing', tabLabel: d.tab_label || d.tab || '?' }); return;
        default:                  setPackLabel({ status: 'error', detail: `Unexpected answer "${d.status}"` });
      }
    } catch {
      setPackLabel({ status: 'error', detail: 'Could not reach the WMS for the label' });
    }
  };

  // Both buttons open the popup INSIDE the click (popup blockers), then print.
  const printFromBanner = async () => {
    if (!packLabel || packLabel.status !== 'ready') return;
    track('button', { name: 'print_label' }, currentOrder?.id);
    const win = window.open('', '_blank');
    if (win) win.document.write('<p style="font-family:sans-serif;padding:2rem">Preparing label…</p>');
    await deliverLabel(win, packLabel.data, null);
    setPackLabel({ status: 'sent', label: packLabel.label, bought: packLabel.bought });
  };
  const printAgain = async () => {
    if (!packLabel || packLabel.status !== 'already_printed') return;
    track('button', { name: 'print_label_again' }, currentOrder?.id);
    if (guidedRef.current) {
      // a guided station: the station's Zebra through the print agent, never a browser popup (review 18.10)
      try {
        const res = await reprintApproval.run(approver => authFetch(`${API}/labels/${packLabel.label.id}/reprint`, {
          method: 'POST', body: JSON.stringify({ format: 'zpl', deliver: 'agent', ...approver }),
        }));
        if (!res) return;                                  // the approval was cancelled
        const d = await res.json().catch(() => ({} as any));
        if (!res.ok) { setPackLabel({ status: 'error', detail: d.detail || 'Reprint failed' }); return; }
        setPackLabel({ status: 'sent', label: packLabel.label });
      } catch {
        setPackLabel({ status: 'error', detail: 'Could not reach the WMS for the reprint' });
      }
      return;
    }
    const win = window.open('', '_blank');
    if (win) win.document.write('<p style="font-family:sans-serif;padding:2rem">Preparing reprint…</p>');
    const zebra = zebraRef.current;
    try {
      const res = await reprintApproval.run(approver => authFetch(`${API}/labels/${packLabel.label.id}/reprint`, {
        method: 'POST', body: JSON.stringify({ format: zebra ? 'zpl' : 'pdf', ...approver }),
      }));
      if (!res) { if (win) win.close(); return; }        // the approval was cancelled
      const d = await res.json().catch(() => ({} as any));
      if (!res.ok) { if (win) win.close(); setPackLabel({ status: 'error', detail: d.detail || 'Reprint failed' }); return; }
      await deliverLabel(win, d, zebra);
      setPackLabel({ status: 'sent', label: packLabel.label });
    } catch {
      if (win) win.close();
      setPackLabel({ status: 'error', detail: 'Could not reach the WMS for the reprint' });
    }
  };

  useEffect(() => {
    if (phase !== 'packing') {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [phase]);


  const loadOrder = useCallback(async (raw: string) => {
    const orderId = raw.trim();
    if (!orderId) return;
    if (labelMatches(lastLabelCodes, orderId)) {
      // Fernando (2026-09-06): the note step must only ever act on a delivery
      // note. This is the parcel label from the order just packed — say so
      // quietly and stay ready for the note.
      setScanInput('');
      setError('');
      setHint('That is the shipping label you just scanned — scan the delivery note of the next order.');
      return;
    }
    setHint('');
    setLoading(true);
    setError('');
    setScanInput('');
    try {
      let fetchId = orderId;
      let resume = false;        // the code was a parcel LABEL of a packed order whose gate was lost (1.11)
      if (guided) {
        // the server says whether this note may be packed here and now (in a picked guided round, this station)
        const g = await authFetch(`${API}/pp2/scan-note`, { method: 'POST', body: JSON.stringify({ code: orderId }) });
        const gb = await g.json().catch(() => ({} as any));
        if (!g.ok) {
          setError(gb.detail || `HTTP ${g.status}`);
          track('note_scan', { ok: false, reason: `guided: ${(gb.detail || 'refused')}`.slice(0, 80), len: orderId.length });
          setLoading(false);
          return;
        }
        fetchId = gb.order_id || orderId;
        resume = gb.action === 'label_gate';
        setBeginnerMultiQty(!!gb.beginner_multi_qty);
        setTips(gb.tips || null);
        setSpecialPlaces(gb.special_places || []);
        // Fernando 21.10: the final move to Can't find happens HERE, by the packer, with the note, the pieces and the
        // label together — never automatically. The order opens as usual; found by now → packed; still missing →
        // Issue → Can't find.
        setShortLines(gb.short_lines || []);
      }
      // scan=1: this is the note-scan entry. Since the WMS-id migration a
      // scanned number can only mean an order number; scan=1 additionally makes
      // the backend explain PRE-MIGRATION barcodes (old notes carried the
      // platform id) instead of answering a bare "not found".
      const res = await authFetch(`${API}/orders/${encodeURIComponent(fetchId)}?scan=1`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({} as any));
        setError(body.detail || `Order "${orderId}" not found`);
        track('note_scan', { ok: false, reason: (body.detail || 'not found').slice(0, 80), len: orderId.length });
        setLoading(false);
        return;
      }
      const o = await res.json();
      if (o.split_hold) {
        // Split shipment between parcels: one has left, the next is not released. There
        // is nothing to pack — the server sends no lines and no labels, and says why.
        setError(o.split_hold);
        track('note_scan', { ok: false, reason: 'split_hold', len: orderId.length }, o.id);
        setLoading(false);
        return;
      }
      setLastOrderId(o.id);
      setCurrentOrder({ id: o.id, order_number: o.order_number,
                        customer: o.customer, carrier: o.carrier, country: o.flag || '' });
      setInstruction(null);
      setCartCard(null);
      const byCode: Record<string, string> = {};
      (o.labels || []).filter((l: any) => !l.cancelled_at).forEach((l: any) =>
        [l.tracking_number, l.tracking_alt, ...(l.scan_codes || [])].filter(Boolean).forEach((c: string) => { byCode[c] = l.carrier || ''; }));
      setLabelCarriers(byCode);
      // Match on the tracking number AND on what a scanner actually reads off
      // the label. They are not always the same: the tracking number is what
      // the customer follows, while some carriers print their own routing code
      // in the barcode. 5 of 22 Packeta labels could not be scanned when only
      // the tracking number was accepted — HU FoxPost prints Packeta's own
      // Z-code, PostNL prints 3SBPB..., and none of those are the number we
      // track by. (Claude, 2026-08-26)
      // Cancelled labels are NOT accepted: with automatic re-buy the old,
      // cancelled label may still be lying in the stack.
      setTrackingNumbers((o.labels || []).filter((l: any) => !l.cancelled_at).flatMap((l: any) =>
        [l.tracking_number, l.tracking_alt, ...(l.scan_codes || [])]).filter(Boolean));
      setLastLabelCodes([]);
      if (resume) {
        // Elsa, round 662 (1.11): an idle sign-out mid-order closed the screen after the order was packed; the label
        // then landed here as "No order 0037…". The server recognised the label — straight to this order's gate.
        setGateNote(tx.labelGateResume(o.order_number || o.id));
        setPhase('label');
        track('note_scan', { ok: true, resume: 'label_gate', labels: (o.labels || []).length }, o.id);
        setLoading(false);
        return;
      }
      setPhase('packing');
      // Step: the delivery note was scanned and the order is on the screen.
      track('note_scan', { ok: true, items: (o.products || []).length,
                           labels: (o.labels || []).length }, o.id);
      // The stored label comes out now, while the items are being packed, so
      // it is waiting at the label gate — or, with nothing stored, the server BUYS one
      // during this request. Fire-and-forget: the banner reports; pack-complete waits.
      requestPackLabel(o.id, (o.labels || []).some((l: any) => !l.cancelled_at));
    } catch {
      setError('Could not load order — check connection');
    }
    setLoading(false);
  }, [lastLabelCodes, requestPackLabel, guided, tx]);

  // Label gate: the scanned parcel label must match one of the order's stored
  // tracking numbers before the next order can be started. If WMS has no
  // tracking on file we cannot verify anything, so a scan is rejected — the
  // packer must fix the label or explicitly skip via the "Next order" button.
  const confirmLabel = useCallback((raw: string) => {
    const scanned = normLabel(raw);
    if (!scanned) return;
    setScanInput('');
    if (guided && tipsRef.current?.note_in_parcel?.show && !noteTickRef.current) {
      setError(tx.noteTickFirst);                  // the tick first — then the label counts
      return;
    }
    const tracks = trackingNumbers.map(normLabel).filter(Boolean);
    if (tracks.length === 0) {
      track('label_scan', { ok: false, reason: 'no label on file' }, currentOrder?.id);
      setError('This order has no label in the WMS — add a label, or press “Next order” to skip.');
      return;
    }
    if (labelMatches(trackingNumbers, raw)) {
      setError('');
      setGateNote('');
      setLastLabelCodes(trackingNumbers);   // so a re-read of this label is recognised, not looked up
      // which cart: the carrier of the label that was scanned + the destination country
      const code = Object.keys(labelCarriers).find(c => labelMatches([c], raw));
      const cart = cartFor(carts, code ? labelCarriers[code] : null, currentOrder?.country);
      track('label_scan', { ok: true, cart }, currentOrder?.id);
      const num = currentOrder?.order_number || currentOrder?.id || '';
      const onLabel = guided?.onLabelScanned;
      setCartCard({ order: num, cart, ...(onLabel ? { fix: { ok: null, text: tx.fixProcessing } } : {}) });
      setPhase('scan_order');   // gate passed → ready for next order's note
      if (onLabel && currentOrder) {
        // a fix: this order is processed now (the station sends its packing steps first)
        onLabel(currentOrder.id, raw).then(res => setCartCard(c => (c && c.order === num && res)
          ? { ...c, fix: { ok: res.ok, text: res.ok ? tx.fixProcessed(num) : tx.fixNotProcessed(num, res.text) } } : c));
      }
      guided?.onProgress?.();
    } else {
      track('label_scan', { ok: false, len: scanned.length }, currentOrder?.id);
      setError('Wrong label — this does not belong to this order. Scan the correct label.');
    }
  }, [trackingNumbers, currentOrder, labelCarriers, carts, guided, tx]);

  // The packing screen's Issue button moved the order to Can't find / Hard to find. The
  // packer now gets the bag instruction (open bag, order number on it, note + label inside,
  // grey box) and confirms it before the next note (Fernando, 2026-09-28).
  const handleIssue = useCallback((info: { tabLabel: string; missing: number }) => {
    setLastLabelCodes([]);
    setError('');
    setInstruction({ kind: 'missing', tab: info.tabLabel });
    setPhase('instruction');
  }, []);

  // No label after packing: the WMS moves the order to Label issue ITSELF and tells the
  // packer to seal the bag and write the order number on it (Fernando, 2026-09-28 — this
  // replaces the 2026-09-06 button and the "skip" that led to a Process now refused).
  // Same endpoint as the grid and the order panel, so the move core's side effects (order
  // history, MCF comment, WGR status mirror) are identical.
  const [moving, setMoving] = useState(false);
  const moveToLabelIssue = useCallback(async (order: OrderInfo | null = currentOrder) => {
    if (!order || moving) return;
    track('button', { name: 'move_to_label_issue', auto: true }, order.id);
    setMoving(true);
    setError('');
    setInstruction({ kind: 'nolabel', moved: false });
    setPhase('instruction');
    try {
      const res = await authFetch(`${API}/orders/move`, {
        method: 'POST',
        body: JSON.stringify({ ids: [order.id], tab: 'label_issue' }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({} as any));
        setInstruction({ kind: 'nolabel', moved: false, error: body.detail || tx.noLabelMoveFailed });
        return;
      }
      setLastLabelCodes([]);
      setInstruction({ kind: 'nolabel', moved: true });
    } catch {
      setInstruction({ kind: 'nolabel', moved: false, error: tx.noLabelMoveFailed });
    } finally {
      setMoving(false);
    }
  }, [currentOrder, moving, tx.noLabelMoveFailed]);

  // Too big (1.11): the gate is where the bag would be — the label is in hand. The box number goes to the server
  // (pp2/too-big), which moves the order to Too Big and keeps the number; the three steps then stay on the screen.
  const startTooBig = useCallback(() => {
    if (!currentOrder) return;
    track('button', { name: 'too_big' }, currentOrder.id);
    setError('');
    setInstruction({ kind: 'too_big', box: null, moved: false });
    setPhase('instruction');
  }, [currentOrder]);
  const pickTooBigBox = useCallback(async (box: number) => {
    if (!currentOrder || !instruction || instruction.kind !== 'too_big' || instruction.busy || instruction.moved) return;
    setInstruction({ kind: 'too_big', box, moved: false, busy: true });
    try {
      const r = await authFetch(`${API}/pp2/too-big`, { method: 'POST', body: JSON.stringify({ order_id: currentOrder.id, box }) });
      const d = await r.json().catch(() => ({} as any));
      if (!r.ok) { setInstruction({ kind: 'too_big', box: null, moved: false, error: d.detail || tx.tooBigMoveFailed }); return; }
      setLastLabelCodes([]);
      setInstruction({ kind: 'too_big', box, moved: true });
      guided?.onProgress?.();
    } catch {
      setInstruction({ kind: 'too_big', box: null, moved: false, error: tx.tooBigMoveFailed });
    }
  }, [currentOrder, instruction, guided, tx]);

  const instructionDone = useCallback(() => {
    track('button', { name: 'instruction_done', kind: instruction?.kind }, currentOrder?.id);
    // a fix (2.11): a piece still missing or no label — the order leaves the fix in its tab, once the bag is done
    if (currentOrder && (instruction?.kind === 'missing' || instruction?.kind === 'nolabel')) {
      guidedRef.current?.onReleased?.(currentOrder.id, instruction.kind === 'missing' ? 'cant_find' : 'label_issue');
    }
    setInstruction(null);
    setError('');
    setPhase('scan_order');
    guided?.onProgress?.();
  }, [instruction, currentOrder, guided]);

  const executeScan = useCallback((val: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    // The one choke point for both input paths (the box and the global
    // listener): a scanner double-fire is dropped here, whatever the phase.
    if (dupRef.current(val)) return;
    if (guided?.intercept && guided.intercept(val.trim())) { setScanInput(''); return; }
    if (phase === 'label') { confirmLabel(val); return; }
    // the missing-item instruction must be read and confirmed; after a no-label move the next
    // note may simply be scanned (that IS the instruction's last line)
    if (phase === 'instruction' && instruction?.kind === 'missing') return;
    if (phase === 'instruction' && instruction?.kind === 'nolabel' && !instruction.moved) return;
    if (phase === 'instruction' && instruction?.kind === 'too_big') return;     // Done first, then the next note
    if (phase === 'instruction' && instruction?.kind === 'nolabel' && currentOrder) {
      guidedRef.current?.onReleased?.(currentOrder.id, 'label_issue');          // a fix: the next note = Done (2.11)
    }
    loadOrder(val);
  }, [phase, instruction, loadOrder, confirmLabel, guided, currentOrder]);

  // Global barcode listener — works even when focus is on a button/elsewhere. The typed characters live in a ref:
  // the listener is re-attached whenever executeScan changes (the guided station re-renders on every poll), and a
  // scan arriving across that moment must not lose its first characters (review 18.10).
  // 28.10: scanWedge — with or without an Enter, physical keys (any keyboard layout); a focused field keeps its keys
  useScanWedge(code => executeScan(code), { enabled: phase !== 'packing', skipFields: true });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setScanInput(v);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (v.trim()) timerRef.current = setTimeout(() => executeScan(v), 150);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (timerRef.current) clearTimeout(timerRef.current);
    executeScan(scanInput);
  };

  const handlePackComplete = useCallback(async (packed?: PackedLine[]) => {
    // Items verified and the note re-scanned: the order is packed. `packed` = what was
    // booked per line; the server keeps it on the order (orders.packed_items) — the
    // ORDER stays what was ordered, this is what shipped (2026-09-21).
    track('order_packed', packed ? { packed } : {}, currentOrder?.id);
    if (trackingRef.current.length === 0 && labelRequest.current) {
      // No label when the note was scanned: the server is buying one inside the label
      // request (2026-09-29). Wait for its answer before deciding — the "buying…" banner
      // is up meanwhile; a bought label lands in trackingNumbers before this resolves.
      await labelRequest.current.catch(() => {});
    }
    if (trackingRef.current.length === 0) {
      // nothing to scan at the gate: every item is verified, the label is what is missing
      moveToLabelIssue(currentOrder);
      return;
    }
    noteTickRef.current = false;
    setNoteTick(false);
    setPhase('label');
  }, [currentOrder, moveToLabelIssue]);

  if (phase === 'instruction' && currentOrder && instruction && instruction.kind === 'too_big') {
    const orderNo = currentOrder.order_number || currentOrder.id;
    const tb = instruction;
    return (
      <div className="fixed inset-0 bg-[#0f1117] z-50 flex flex-col">
        <div className="flex-none bg-[#1a1d27] border-b border-gray-800 px-6 py-4 flex items-center gap-4">
          <button onClick={onClose} className="text-gray-400 hover:text-white text-sm px-3 py-1.5 bg-gray-800 rounded-lg">✕ Close</button>
          <span className="text-white font-bold">{orderNo}</span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-6 gap-6 overflow-y-auto">
          <div className="w-full max-w-xl rounded-2xl border-2 border-amber-600 bg-amber-900/15 p-6">
            <div className="text-xl font-bold text-amber-200">{tx.tooBigTitle(orderNo)}</div>
            <ol className="mt-4 space-y-3">
              <li className="flex items-start gap-3 text-white text-2xl leading-snug">
                <span className="flex-none w-9 h-9 rounded-full flex items-center justify-center text-base font-bold bg-amber-600">1</span>
                <span>
                  {tx.tooBigPick}
                  <span className="mt-3 flex gap-3">
                    {[1, 2, 3].map(n => (
                      <button key={n} type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()}
                        disabled={tb.busy || tb.moved} onClick={() => pickTooBigBox(n)}
                        className={`w-24 h-20 rounded-2xl text-4xl font-black border-2 ${tb.box === n ? 'bg-amber-500 border-amber-300 text-black' : 'bg-gray-800 border-gray-600 text-white'} disabled:opacity-60`}>
                        {n}
                      </button>
                    ))}
                  </span>
                </span>
              </li>
              {tx.tooBigSteps.map((s, i) => (
                <li key={i} className={`flex items-start gap-3 text-2xl leading-snug ${tb.moved ? 'text-white' : 'text-gray-500'}`}>
                  <span className={`flex-none w-9 h-9 rounded-full flex items-center justify-center text-base font-bold ${tb.moved ? 'bg-amber-600' : 'bg-gray-700'}`}>{i + 2}</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
            <div className={`mt-5 rounded-lg px-4 py-3 text-lg ${tb.moved ? 'bg-emerald-800/40 text-emerald-100' : tb.error ? 'bg-red-900/40 text-red-200' : tb.busy ? 'bg-gray-800 text-gray-300' : 'hidden'}`}>
              {tb.moved && tb.box ? tx.tooBigMoved(orderNo, tb.box) : tb.error ? tb.error : tb.busy ? '…' : ''}
            </div>
          </div>
          <div className="flex gap-3">
            {!tb.moved && (
              <button onClick={() => { setInstruction(null); setPhase('label'); }}
                className="px-8 py-4 rounded-xl bg-gray-700 hover:bg-gray-600 text-white text-xl font-bold">{tx.cancel}</button>
            )}
            <button onClick={instructionDone} disabled={!tb.moved}
              className="px-10 py-4 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white text-xl font-bold">
              {tx.done}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'instruction' && currentOrder && instruction) {
    const orderNo = currentOrder.order_number || currentOrder.id;
    const missing = instruction.kind === 'missing';
    const steps = missing ? tx.missingSteps : tx.noLabelSteps;
    return (
      <div className="fixed inset-0 bg-[#0f1117] z-50 flex flex-col" onClick={() => inputRef.current?.focus()}>
        <div className="flex-none bg-[#1a1d27] border-b border-gray-800 px-6 py-4 flex items-center gap-4">
          <button onClick={onClose} className="text-gray-400 hover:text-white text-sm px-3 py-1.5 bg-gray-800 rounded-lg">✕ Close</button>
          <span className="text-white font-bold">{orderNo}</span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-6 gap-6 overflow-y-auto">
          <div className={`w-full max-w-xl rounded-2xl border-2 p-6 ${missing ? 'border-amber-600 bg-amber-900/15' : 'border-emerald-600 bg-emerald-900/15'}`}>
            <div className={`text-xl font-bold ${missing ? 'text-amber-200' : 'text-emerald-200'}`}>
              {missing ? tx.missingTitle(orderNo, instruction.tab) : tx.noLabelTitle}
            </div>
            <ol className="mt-4 space-y-3">
              {steps.map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-white text-2xl leading-snug">
                  <span className={`flex-none w-9 h-9 rounded-full flex items-center justify-center text-base font-bold ${missing ? 'bg-amber-600' : 'bg-emerald-600'}`}>{i + 1}</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
            {!missing && (
              <div className={`mt-5 rounded-lg px-4 py-3 text-lg ${instruction.moved ? 'bg-emerald-800/40 text-emerald-100' : instruction.error ? 'bg-red-900/40 text-red-200' : 'bg-gray-800 text-gray-300'}`}>
                {instruction.moved ? tx.noLabelMoved(orderNo) : instruction.error ? instruction.error : 'Moving to Label issue…'}
                {instruction.error && (
                  <button onClick={() => moveToLabelIssue(currentOrder)} disabled={moving}
                    className="ml-3 px-3 py-1.5 rounded bg-red-700 text-white text-sm">{tx.retry}</button>
                )}
              </div>
            )}
          </div>
          <button onClick={instructionDone} disabled={!missing && !instruction.moved}
            className="px-10 py-4 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white text-xl font-bold">
            {tx.done}
          </button>
          <form onSubmit={handleSubmit} className="w-full max-w-sm">
            <input ref={inputRef} value={scanInput} onChange={handleChange}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-xl px-4 py-3 text-center text-lg focus:outline-none"
              placeholder={missing || !instruction.moved ? '' : tx.scanNext} autoComplete="off" />
          </form>
        </div>
      </div>
    );
  }

  if (phase === 'packing' && currentOrder) {
    const base = 'mx-6 mt-4 rounded-xl border px-4 py-2.5 text-sm flex items-center justify-between gap-4';
    const lbl = (l: any) => l ? `${l.tracking || ''}${l.carrier ? ` (${l.carrier})` : ''}` : '';
    const btn = 'flex-none px-4 py-1.5 text-white text-sm font-bold rounded-lg transition-colors';
    let banner: React.ReactNode = null;
    if (packLabel) {
      switch (packLabel.status) {
        case 'checking':
          banner = <div className={`${base} border-gray-800 bg-[#1a1d27] text-gray-400`}>Checking the shipping label…</div>; break;
        case 'buying':
          banner = <div className={`${base} border-amber-800/50 bg-amber-900/20 text-amber-200`}>No stored shipping label — buying one from the carrier now… pack as usual, the gate waits for it.</div>; break;
        case 'sent':
          banner = <div className={`${base} border-green-800/50 bg-green-900/20 text-green-300`}>
                     <span>{packLabel.bought ? 'New shipping label bought and printed ✓' : 'Shipping label printed ✓'}
                       {' '}<span className="text-green-200/70">{lbl(packLabel.label)}</span></span></div>; break;
        case 'ready':
          banner = <div className={`${base} border-blue-800/50 bg-blue-900/20 text-blue-200`}>
                     <span>{packLabel.note || (packLabel.bought ? 'New shipping label bought' : 'Shipping label ready')} — {lbl(packLabel.label)}</span>
                     <button onClick={printFromBanner} className={`${btn} bg-blue-600 hover:bg-blue-500`}>Print label</button></div>; break;
        case 'dispatched':
          banner = <div className={`${base} border-amber-800/50 bg-amber-900/20 text-amber-200`}>
                     <span>Dispatched{packLabel.at ? ` on ${fmtWh(packLabel.at, 'short')}` : ''}
                       {packLabel.previous ? ` with label ${lbl(packLabel.previous)}` : ''} — buying a new label…</span></div>; break;
        case 'buy_failed':
          banner = <div className={`${base} border-red-800/40 bg-red-900/20 text-red-300`}>
                     {packLabel.at
                       ? <>Dispatched on {fmtWh(packLabel.at, 'short')} — buying a new label failed: {packLabel.detail}. Use Print shipping label on the order.</>
                       : <>Buying a shipping label failed: {packLabel.detail}. Pack and scan everything as usual — the order then goes to Label issue by itself.</>}</div>; break;
        case 'already_printed':
          banner = <div className={`${base} border-amber-800/50 bg-amber-900/20 text-amber-200`}>
                     <span>Label already printed{packLabel.at ? ` at ${fmtTime(packLabel.at)}` : ''}{packLabel.by ? ` by ${packLabel.by}` : ''} — {lbl(packLabel.label)}. It is in the stack; nothing printed now.</span>
                     <button onClick={printAgain} className={`${btn} bg-amber-700 hover:bg-amber-600`}>Print again</button></div>; break;
        case 'no_label':
          banner = <div className={`${base} border-gray-800 bg-[#1a1d27] text-gray-400`}>No shipping label for this order — pack and scan everything as usual; it then goes to Label issue by itself.</div>; break;
        case 'stale':
          // a fix (2.11): the automatic re-buy runs only in New / Waiting for NOS / Printed / Picker 2 — not here
          banner = <div className={`${base} border-red-800/40 bg-red-900/20 text-red-300`}>Order changed in the shop after its label was bought{packLabel.detail ? ` (${packLabel.detail})` : ''}.{' '}
                     {guided?.onLabelScanned
                       ? 'Its label must not be used and is not re-bought here — make a new label from the order, then pack it.'
                       : 'A new label is being bought automatically — do not use an old one; scan this note again in a minute.'}</div>; break;
        case 'not_first_packing':
          banner = <div className={`${base} border-gray-800 bg-[#1a1d27] text-gray-400`}>In {packLabel.tabLabel} — not first-time packing, so no label is printed automatically.</div>; break;
        case 'error':
          banner = <div className={`${base} border-red-800/40 bg-red-900/20 text-red-300`}>Label check failed: {packLabel.detail}</div>; break;
      }
    }
    return (
      <>
        <PackingWindow
          orderIds={[currentOrder.id]}
          scanMode
          beginnerMultiQty={!!guided && beginnerMultiQty}
          tips={guided ? (tips || undefined) : undefined}
          shortLines={guided ? shortLines : []}
          specialPlaces={specialPlaces}
          onPackComplete={handlePackComplete}
          onClose={onClose}
          labelBanner={banner}
          onIssue={handleIssue}
          manualGate={guided?.manualGate
          ? (line => guided.manualGate!({ ...line, order_id: currentOrder?.id }))
          : (async line => {
              const r = await manualApproval.run(approver => authFetch(`${API}/pp2/pack-manually`, {
                method: 'POST', body: JSON.stringify({ ...line, order_id: currentOrder?.id,
                                                       actor: trackActor() || undefined, ...approver }) }));
              if (!r) return false;                          // cancelled: nothing packed
              if (!r.ok) {
                const d = await r.json().catch(() => ({} as any));
                alert(typeof d.detail === 'string' ? d.detail : `Pack without scanning refused (HTTP ${r.status})`);
                return false;
              }
              return true;
            })}
        />
        {reprintApproval.box}
        {manualApproval.box}
      </>
    );
  }

  const isLabel = phase === 'label';
  const hasTracking = trackingNumbers.length > 0;

  return (
    <div
      className="fixed inset-0 bg-[#0f1117] z-50 flex flex-col"
      onClick={() => inputRef.current?.focus()}
    >
      <div className="flex-none bg-[#1a1d27] border-b border-gray-800 px-6 py-4 flex items-center gap-4">
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white text-sm px-3 py-1.5 bg-gray-800 rounded-lg"
        >
          ✕ Close
        </button>
        <span className="text-white font-bold">
          {isLabel ? 'Scan label' : 'Packing'}
        </span>
        {/* the live speed delta of the round this order belongs to (Fernando, 2026-09-28);
            stays on screen between orders — the last order's round is still the round */}
        {lastOrderId && (
          <div className="ml-auto">
            <SpeedChipLive src={{ orderId: lastOrderId }} person={trackActor()} theme="dark" refreshKey={`${phase}:${currentOrder?.id || ''}`} />
          </div>
        )}
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-8 gap-8">

        {guided?.header && !isLabel && <div className="w-full max-w-2xl">{guided.header}</div>}

        {isLabel && currentOrder && (
          <div className="bg-[#1a1d27] border border-gray-700 rounded-2xl px-12 py-8 w-full max-w-sm space-y-5 text-center">
            <div className={`text-xs uppercase tracking-widest ${hasTracking ? 'text-gray-500' : 'text-red-400'}`}>
              {hasTracking ? 'Scan the label on the parcel' : 'No label on file for this order'}
            </div>
            {gateNote && <div className="rounded-lg border border-amber-700 bg-amber-900/20 px-3 py-2 text-amber-200 text-base font-semibold">{gateNote}</div>}
            {guided?.newNotes?.includes(currentOrder.id) && (
              // a fix printed this order a new delivery note: the old one in the bag is out of date (2.11)
              <div className="rounded-lg border border-amber-500 bg-amber-500/20 px-3 py-2 text-amber-100 text-base font-bold">{tx.fixNewNote}</div>
            )}
            <div>
              <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">Order</div>
              <OrderNo value={currentOrder.order_number || currentOrder.id}
                       className="text-white text-5xl font-black" />
            </div>
            <div>
              <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">Customer</div>
              <div className="text-white text-xl font-semibold">{currentOrder.customer}</div>
            </div>
            <div>
              <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">Delivery</div>
              <div className="text-blue-300 text-lg font-medium">{currentOrder.carrier}</div>
            </div>
          </div>
        )}

        {!isLabel && cartCard && (
          // the parcel just labelled: which cart it goes into (Fernando, 2026-09-28)
          <div className="w-full max-w-md rounded-2xl border-2 border-emerald-600 bg-emerald-900/15 px-6 py-5 text-center">
            <div className="text-emerald-300 text-xs uppercase tracking-widest">{cartCard.order} · {tx.cartLabel}</div>
            <div className="mt-2 inline-block text-4xl font-black bg-gray-700 text-gray-100 px-6 py-2 rounded-lg">{cartCard.cart}</div>
            <div className="text-emerald-100 text-lg mt-2">{tx.cartLine(cartCard.cart)}</div>
            {cartCard.fix && (
              <div className={`mt-3 rounded-lg px-3 py-2 text-lg font-bold ${cartCard.fix.ok === null ? 'bg-gray-800 text-gray-300'
                : cartCard.fix.ok ? 'bg-emerald-700 text-white' : 'bg-red-800 text-white'}`}>{cartCard.fix.text}</div>
            )}
          </div>
        )}

        {!isLabel && (
          <div className="text-center space-y-3">
            {!cartCard && <div className="text-8xl leading-none mb-2 opacity-30">▦</div>}
            <div className="text-white text-2xl font-bold">{cartCard ? tx.scanNext : 'Scan order barcode'}</div>
            <div className="text-gray-500 text-sm">
              Scan the barcode on the delivery note to start packing
            </div>
          </div>
        )}

        {isLabel && hasTracking && (
          // the parcel in four steps (Fernando 21.10: EE19537 left without its delivery note)
          <ol className="w-full max-w-md space-y-2">
            {tx.parcelSteps.map((s, i) => (
              <li key={i} className={`flex items-center gap-3 rounded-xl px-4 py-3 text-lg font-bold ${
                i === 0 ? 'bg-amber-500/20 border border-amber-500/50 text-amber-100' : 'bg-gray-800/70 text-gray-200'}`}>
                <span className="w-8 h-8 rounded-full bg-gray-900 flex items-center justify-center text-base">{i + 1}</span>{s}
              </li>
            ))}
          </ol>
        )}
        {isLabel && hasTracking && guided && tips?.note_in_parcel?.show && (
          // never focused: a scanner's Enter must not press it
          <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()}
            onClick={() => {
              if (noteTickRef.current) return;
              noteTickRef.current = true; setNoteTick(true); setError('');
              track('button', { name: 'tip_ack', tip: 'note_in_parcel' }, currentOrder?.id);
            }}
            className={`w-full max-w-md rounded-xl px-5 py-4 text-xl font-black ${noteTick ? 'bg-emerald-700 text-white' : 'bg-amber-400 text-black'}`}>
            {noteTick ? '✓' : '☐'} {tx.noteTick}
          </button>
        )}
        {isLabel && !hasTracking && (
          <div className="text-gray-400 text-sm text-center max-w-sm">This order has no label in the WMS — it goes to Label issue.</div>
        )}
        {isLabel && guided && (
          // too big for a bag (Fernando 1.11): at the gate the label is in hand — the packer boxes the whole order
          // for a manager instead of scanning the label; never focused, a scanner's Enter must not press it
          <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={startTooBig}
            className="w-full max-w-md rounded-xl px-5 py-3 text-lg font-bold border-2 border-amber-700 text-amber-200 bg-amber-900/15 hover:bg-amber-900/30">
            📦 {tx.tooBigBtn}
          </button>
        )}

        {error && (
          <div className="text-red-400 text-sm bg-red-900/20 border border-red-800/40 rounded-lg px-4 py-2 max-w-sm w-full text-center">
            {error}
          </div>
        )}
        {hint && !error && (
          <div className="text-gray-400 text-sm bg-gray-800/60 border border-gray-700 rounded-lg px-4 py-2 max-w-sm w-full text-center">
            {hint}
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full max-w-sm">
          <input
            ref={inputRef}
            value={scanInput}
            onChange={handleChange}
            disabled={loading}
            className="w-full bg-gray-800 border border-gray-700 focus:border-blue-500 text-white rounded-xl px-4 py-4 text-center text-lg focus:outline-none disabled:opacity-50"
            placeholder={loading ? 'Loading…' : 'Scan barcode…'}
            autoComplete="off"
          />
        </form>

        {isLabel && (
          <div className="flex items-center gap-3">
            {!hasTracking && (
              // only reachable if the gate was entered without codes (a label bought and lost
              // mid-session): the same automatic move as the packing step's
              <button
                onClick={() => moveToLabelIssue(currentOrder)}
                disabled={moving}
                className="px-8 py-3 bg-orange-600 hover:bg-orange-500 disabled:opacity-50 text-white text-sm font-bold rounded-lg transition-colors"
              >
                {moving ? 'Moving…' : 'Move to Label issue'}
              </button>
            )}
            <button
              onClick={onClose}
              className="px-8 py-3 bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Done — close session
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
